import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField,
} from '@mui/material';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function browserTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata'; } catch (e) { return 'Asia/Kolkata'; }
}

const EMPTY = {
  title: '', prompt: '', conversation_id: '', kind: 'daily', time_of_day: '09:00', weekday: 1, timezone: browserTimezone(),
};

/** Create or edit a schedule: a message that is sent to one chat at a set time. `schedule` null means create. */
export default function ScheduleDialog({
  open, schedule, chats, saving, error, onClose, onSave,
}) {
  const [form, setForm] = useState(EMPTY);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (!open) return;
    setLocalError('');
    if (schedule) {
      setForm({
        title: schedule.title,
        prompt: schedule.prompt,
        conversation_id: schedule.conversation.id,
        kind: schedule.kind,
        time_of_day: schedule.time_of_day,
        weekday: schedule.weekday ?? 1,
        timezone: schedule.timezone,
      });
    } else {
      const defaultChat = chats.find((c) => c.type === 'group') || chats[0];
      setForm({ ...EMPTY, conversation_id: defaultChat?.id || '', timezone: browserTimezone() });
    }
  }, [open, schedule, chats]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (form.title.trim().length < 2) { setLocalError('Give the schedule a name.'); return; }
    if (form.prompt.trim().length < 3) { setLocalError('Write the message to send.'); return; }
    if (!form.conversation_id) { setLocalError('Choose a chat.'); return; }
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(form.time_of_day)) { setLocalError('Choose a time.'); return; }
    setLocalError('');
    const body = {
      title: form.title.trim(),
      prompt: form.prompt.trim(),
      conversation_id: form.conversation_id,
      kind: form.kind,
      time_of_day: form.time_of_day,
      timezone: form.timezone,
    };
    if (form.kind === 'weekly') body.weekday = Number(form.weekday);
    onSave(body);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth component="form" onSubmit={submit}>
      <DialogTitle>{schedule ? 'Edit schedule' : 'New schedule'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {(localError || error) && <Alert severity="error">{localError || error}</Alert>}
          <TextField label="Name" value={form.title} onChange={set('title')} fullWidth autoFocus placeholder="Morning brief" inputProps={{ maxLength: 120 }} />
          <TextField
            label="Message to send"
            value={form.prompt}
            onChange={set('prompt')}
            multiline
            minRows={3}
            fullWidth
            placeholder="Give me a short brief: new orders, low stock and anything that needs my attention."
            helperText="Sent to the chat below as if you wrote it. The team answers there."
            inputProps={{ maxLength: 2000 }}
          />
          <TextField select label="Send it to" value={form.conversation_id} onChange={set('conversation_id')} fullWidth>
            {chats.map((c) => <MenuItem key={c.id} value={c.id}>{c.type === 'group' ? `${c.title} (whole team)` : c.title}</MenuItem>)}
          </TextField>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField select label="Repeat" value={form.kind} onChange={set('kind')} fullWidth>
              <MenuItem value="daily">Every day</MenuItem>
              <MenuItem value="weekdays">Every weekday (Mon to Fri)</MenuItem>
              <MenuItem value="weekly">Every week</MenuItem>
            </TextField>
            {form.kind === 'weekly' && (
              <TextField select label="On" value={form.weekday} onChange={set('weekday')} fullWidth>
                {DAYS.map((d, i) => <MenuItem key={d} value={i}>{d}</MenuItem>)}
              </TextField>
            )}
            <TextField label="At" type="time" value={form.time_of_day} onChange={set('time_of_day')} InputLabelProps={{ shrink: true }} sx={{ minWidth: 130 }} />
          </Stack>
          <TextField label="Timezone" value={form.timezone} onChange={set('timezone')} fullWidth helperText="For example Asia/Kolkata" />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={saving}>{schedule ? 'Save' : 'Create schedule'}</Button>
      </DialogActions>
    </Dialog>
  );
}
